/**
 * BR 纯工具（无 Vue 依赖）。
 * 本地日历日 + 列表排序时间。
 */

export function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * @param {string|Date|number|null|undefined} raw
 * @returns {Date|null} 本地日 00:00:00；无法解析则 null
 */
export function parseBrLocalDate(raw) {
  if (raw == null || raw === '') {
    return null;
  }
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) {
      return null;
    }
    return startOfLocalDay(raw);
  }
  if (typeof raw === 'number') {
    const fromNumber = new Date(raw);
    if (Number.isNaN(fromNumber.getTime())) {
      return null;
    }
    return startOfLocalDay(fromNumber);
  }

  const text = String(raw).trim();
  if (!text) {
    return null;
  }

  const fromParts = (year, month, day) => {
    if (!year || month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }
    const local = new Date(year, month - 1, day);
    if (
      Number.isNaN(local.getTime())
      || local.getFullYear() !== year
      || local.getMonth() !== month - 1
      || local.getDate() !== day
    ) {
      return null;
    }
    return local;
  };

  const expandYear = (yearRaw) => {
    const year = Number(yearRaw);
    if (!year) return 0;
    if (yearRaw.length === 2) {
      return year >= 70 ? 1900 + year : 2000 + year;
    }
    return year;
  };

  /* YYYY-MM-DD / YYYY/MM/DD / YYYY.MM.DD */
  const isoMatch = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b/.exec(text);
  if (isoMatch) {
    return fromParts(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]));
  }

  /* 中文日期：2020年1月1日 */
  const cnMatch = /^(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?/.exec(text);
  if (cnMatch) {
    return fromParts(Number(cnMatch[1]), Number(cnMatch[2]), Number(cnMatch[3]));
  }

  /* DD-MMM-YYYY / DD MMM YYYY（如 20-May-1998） */
  const monthNameMatch = /^(\d{1,2})[-\s/]+([A-Za-z\u00C0-\u024F]+)[-\s/]+(\d{2,4})\b/.exec(text);
  if (monthNameMatch) {
    const monthToken = monthNameMatch[2].toLowerCase().slice(0, 3);
    const monthMap = {
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    };
    const month = monthMap[monthToken];
    if (month) {
      return fromParts(
        expandYear(monthNameMatch[3]),
        month,
        Number(monthNameMatch[1]),
      );
    }
  }

  /*
   * DD/MM/YYYY、DD-MM-YYYY、DD.MM.YYYY（全球证照常见）。
   * 若第一段 > 12，必为日在前；两段均 ≤12 时默认日在前，避免与美式混淆。
   */
  const dmyMatch = /^(\d{1,2})([-/.])(\d{1,2})\2(\d{2,4})\b/.exec(text);
  if (dmyMatch) {
    const first = Number(dmyMatch[1]);
    const second = Number(dmyMatch[3]);
    const year = expandYear(dmyMatch[4]);
    if (first > 12 && second >= 1 && second <= 12) {
      return fromParts(year, second, first);
    }
    if (second > 12 && first >= 1 && first <= 12) {
      return fromParts(year, first, second);
    }
    if (first <= 12 && second <= 12) {
      return fromParts(year, second, first);
    }
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return startOfLocalDay(parsed);
}

/**
 * @param {Date} date
 * @returns {string} YYYY-MM-DD（本地）
 */
export function formatBrLocalIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * 「有效期至」最早可选日：今天（含当天）
 * @returns {string} YYYY-MM-DD
 */
export function getBrExpiryDateMinIso() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return formatBrLocalIsoDate(today);
}

/**
 * 本地化长日期（如 21 July 2026 / 2026年7月21日）
 * @param {Date|null|undefined} date
 * @param {string} [locale]
 * @returns {string}
 */
export function formatBrLocaleLongDate(date, locale) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  try {
    const lang = locale === 'zhHK' ? 'zh-HK' : locale === 'zhCN' ? 'zh-CN' : 'en-GB';
    return new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch (e) {
    return formatBrLocalIsoDate(date);
  }
}

/**
 * 列表/选项排序用时间戳。
 * 优先级：updatedDate → submittedDate → createdDate（含 PascalCase）。
 */
export function getItemLastUpdatedTime(item) {
  if (!item) {
    return 0;
  }
  const raw = item.updatedDate || item.UpdatedDate
    || item.submittedDate || item.SubmittedDate
    || item.createdDate || item.CreatedDate
    || null;
  if (!raw) {
    return 0;
  }
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function getItemExpiryDate(item) {
  return parseBrLocalDate(item?.expiryDate || item?.ExpiryDate);
}

export function getItemIssueDate(item) {
  return parseBrLocalDate(item?.issueDate || item?.IssueDate);
}

export function isAfterToday(date) {
  return startOfLocalDay(date) > startOfLocalDay(new Date());
}

export function isBeforeToday(date) {
  return startOfLocalDay(date) < startOfLocalDay(new Date());
}

export function daysUntil(date) {
  const ms = startOfLocalDay(date).getTime() - startOfLocalDay(new Date()).getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000));
}
